import { getViews, PanelView, SceneView } from '@rendering/lookup';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { BookletDisplayOrientation, REQUESTOR } from 'src/utils/Constants';
import { noticeError } from 'src/utils/newRelicUtil';

export const getPreviewUser = () => {
  return {
    tenant: { id: 'vistaprint-prod', type: 'merchants' },
    referer: REQUESTOR,
  };
};

const getViewsByType = async (
  previewType: 'scene' | 'panel',
  productKey: string,
  selectedOptions: Record<string, string>
) => {
  try {
    const views = await getViews({
      authorizationHeader: getAuthHeader().headers.Authorization,
      request: {
        reference: { id: productKey, attributes: selectedOptions },
        tenant: getPreviewUser().tenant,
        previewType: [previewType],
        purpose: 'Product',
        subPurpose: 'Full',
      },
    });

    return views;
  } catch (err) {
    noticeError(err, { method: 'getViewsByType' });
    throw err;
  }
};

export const fetchViews = async (
  productKey: string,
  selectedOptions: Record<string, string>
): Promise<{ sceneViews: SceneView[]; panelViews: PanelView[] }> => {
  try {
    const sceneViewsPromise = getViewsByType('scene', productKey, selectedOptions);
    const panelViewsPromise = getViewsByType('panel', productKey, selectedOptions);

    const [sceneViews, panelViews] = await Promise.all([sceneViewsPromise, panelViewsPromise]);

    return { sceneViews, panelViews } as { sceneViews: SceneView[]; panelViews: PanelView[] };
  } catch (err) {
    noticeError(err, { method: 'fetchViews', productKey, selectedOptions });
    throw err;
  }
};

interface PageLabelResult {
  topPositionedPageLabels: string[] | undefined;
  bottomPositionedPageLabels: string[] | undefined;
}

// Determine and return labels positioned at the top and bottom according to the booklet display orientation and the provided labels.
export function getPositionedPageLabels(
  labels: string[] | undefined,
  bookletDisplayOrientation: BookletDisplayOrientation | undefined
): PageLabelResult {
  const isVerticalOrientation = bookletDisplayOrientation === BookletDisplayOrientation.Vertical;

  const hasMultipleLabels = labels && labels.length > 1;

  // Vertical orientation with multiple labels
  // → first label on top, last label on bottom
  if (isVerticalOrientation && hasMultipleLabels) {
    return {
      topPositionedPageLabels: [labels[0]],
      bottomPositionedPageLabels: [labels[labels.length - 1]],
    };
  }

  // Horizontal orientation or single label
  // → no top label, all labels at bottom
  return {
    topPositionedPageLabels: undefined,
    bottomPositionedPageLabels: labels,
  };
}
